import React, { useState, useMemo, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Import the CSS file we just made
import "./TrackerProgressChart.css";

const METRICS = [
  { key: "view_count", label: "Views", color: "#11998e" },
  { key: "like_count", label: "Likes", color: "#e1306c" },
  { key: "comments", label: "Comments", color: "#1976d2" },
  //   { key: "shares", label: "Shares", color: "#ed6c02" },
];

export default function TrackerProgressChart({ historyData }) {
  const [selectedUrl, setSelectedUrl] = useState("");
  const [selectedMetric, setSelectedMetric] = useState("view_count");

  // Auto-select the first URL when data arrives
  useEffect(() => {
    if (historyData?.length > 0 && !selectedUrl) {
      setSelectedUrl(historyData[0].url);
    }
  }, [historyData, selectedUrl]);

  // Process data for the chart (sort chronologically and format dates)
  const chartData = useMemo(() => {
    if (!historyData || historyData.length === 0) return [];

    const targetGroup = historyData.find((item) => item.url === selectedUrl);
    if (!targetGroup) return [];

    return [...targetGroup.data]
      .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at))
      .map((record) => ({
        ...record,
        displayDate: new Date(record.recorded_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      }));
  }, [historyData, selectedUrl]);

  if (!historyData || historyData.length === 0) {
    return null; // Don't render anything if there's no history yet
  }

  const activeMetricObj = METRICS.find((m) => m.key === selectedMetric);

  return (
    <div className="tracker-chart-container">
      {/* Header & Controls */}
      <div className="chart-header">
        <h6 className="chart-title">Performance Growth</h6>

        <div className="chart-controls">
          {/* Metric Select */}
          <div className="control-group">
            <label className="control-label">Metric</label>
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="control-select"
            >
              {METRICS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Link Select */}
          <div className="control-group control-group-link">
            <label className="control-label">Link</label>
            <select
              value={selectedUrl}
              onChange={(e) => setSelectedUrl(e.target.value)}
              className="control-select truncate-text"
            >
              {historyData.map((group) => (
                <option key={group.url} value={group.url}>
                  {group.url.length > 35
                    ? group.url.substring(0, 35) + "..."
                    : group.url}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-body">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e5e7eb"
            />
            <XAxis
              dataKey="displayDate"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#6b7280", fontSize: 12 }}
              dy={10}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#6b7280", fontSize: 12 }}
              tickFormatter={(value) =>
                new Intl.NumberFormat("en-US", { notation: "compact" }).format(
                  value,
                )
              }
            />
            <Tooltip
              contentStyle={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                fontWeight: 600,
              }}
            />
            <Line
              type="monotone"
              dataKey={selectedMetric}
              stroke={activeMetricObj?.color || "#11998e"}
              strokeWidth={3}
              dot={{ r: 4, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
              animationDuration={1500}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
